import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { finalize } from "rxjs";

// Material imports for dialog
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

// Dialog component
import { DialogComponent } from '../../dialog/dialog.component';

import { CoreService } from "../../../services/core.service";

interface SiteOption {
  id: number;
  domain: string;
}

interface TikTokConnectionStatus {
  site_id: number;
  platform_id: number;  // Store the actual platform ID for disconnection
  connected: boolean;
  social_media_id: string | null;
  connected_at: string | null;
  last_validated_at: string | null;
}

@Component({
  selector: "publio-tiktok-connection",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
  ],
  templateUrl: "./publio-tiktok-connection.component.html",
  styleUrl: "./publio-tiktok-connection.component.scss",
})
export class PublioTiktokConnectionComponent
  implements OnInit, OnChanges {

  @Input() companyID: string | null = null;
  @Input() preselectedPlatformId: string | null = null;
  @Input() preselectedSiteId: string | null = null;
  
  @Output() platformDisconnected = new EventEmitter<void>();

  sites: SiteOption[] = [];

  selectedSiteId: number | null = null;
  
  // Track pending preselection to handle cases where preselection happens before sites are loaded
  private pendingPreselectedSiteId: string | null = null;
  private pendingPreselectedPlatformId: string | null = null;

  loadingConnect = false;
  connected = false;
  loadingStatus = false;

  connectionStatus: TikTokConnectionStatus | null = null;

  errorMessage = "";

  constructor(
    private coreService: CoreService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Initialize component
  }

  /**
   * Shows a confirmation dialog for TikTok disconnection
   * @param isLocalOnly Whether this is a local-only disconnection
   * @returns Promise that resolves to true if user confirms, false otherwise
   */
  private showDisconnectConfirmation(isLocalOnly: boolean = false): Promise<boolean> {
    return new Promise((resolve) => {
      let title, message;
      
      if (isLocalOnly) {
        title = 'Confirm Site Disconnection';
        message = `
          <p class="text-gray-700 mb-4">
            Are you sure you want to disconnect this site from TikTok?
          </p>
          <p class="text-gray-500 text-sm mb-6">
            Your other sites will remain connected to this TikTok account. This will only remove the connection for this specific site.
          </p>
        `;
      } else {
        title = 'Confirm Full Disconnection';
        message = `
          <p class="text-gray-700 mb-4">
            Are you sure you want to disconnect this TikTok account?
          </p>
          <p class="text-gray-500 text-sm mb-6">
            This will remove the connection from ALL your sites and revoke access to your TikTok account.
          </p>
        `;
      }

      const dialogRef = this.dialog.open(DialogComponent, {
        width: '450px',
        maxWidth: '95vw',
        data: {
          title: title,
          htmlContent: `<div class="py-2">${message}</div>`,
          showClose: false,  // Hide the default close button since we have our own
          buttons: [
            {
              text: 'No',
              type: 'secondary',
              action: false,
              class: 'px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 min-w-[80px] font-medium'
            },
            {
              text: 'Yes',
              type: isLocalOnly ? 'primary' : 'danger',
              action: true,
              class: `${isLocalOnly ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-500 border-b-4 border-orange-700' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 focus:ring-offset-2'} text-white rounded-lg focus:outline-none focus:ring-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 min-w-[80px] font-medium px-4 py-2`
            }
          ]
        }
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        resolve(result === true);
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["companyID"] &&
      this.companyID
    ) {
      this.loadSites();
    }
    
    // Handle preselected site ID (takes precedence over platform ID)
    if (changes["preselectedSiteId"]) {
      if (this.preselectedSiteId) {
        // If sites are already loaded, process immediately
        if (this.sites.length > 0) {
          this.handlePreselectedSite(this.preselectedSiteId!);
        } else {
          // Store for processing after sites load
          this.pendingPreselectedSiteId = this.preselectedSiteId;
        }
      } else {
        // Clear selection if preselectedSiteId becomes null
        this.selectedSiteId = null;
        this.connectionStatus = null;
        this.connected = false;
      }
    }
    // Handle preselected platform ID (fallback if site ID not provided)
    else if (changes["preselectedPlatformId"]) {
      if (this.preselectedPlatformId) {
        // If sites are already loaded, process immediately
        if (this.sites.length > 0) {
          this.handlePreselectedPlatform(this.preselectedPlatformId!);
        } else {
          // Store for processing after sites load
          this.pendingPreselectedPlatformId = this.preselectedPlatformId;
        }
      } else {
        // Clear selection if preselectedPlatformId becomes null
        this.selectedSiteId = null;
        this.connectionStatus = null;
        this.connected = false;
      }
    }
  }

  onSiteChange(siteId: number | null): void {
    this.selectedSiteId = siteId;
    this.connectionStatus = null;
    this.connected = false;
    this.errorMessage = "";

    if (siteId) {
      this.loadConnectionStatus(siteId);
    }
  }

  connect(): void {
    if (!this.selectedSiteId) {
      this.errorMessage =
        "Select a Publio Site before connecting TikTok.";
      return;
    }

    this.loadingConnect = true;
    this.errorMessage = "";

    this.coreService.postData('tiktok/save-and-login', {
      company_id: Number(this.companyID),
      site_id: this.selectedSiteId,
    })
      .pipe(
        finalize(() => {
          this.loadingConnect = false;
        })
      )
      .subscribe({
        next: (res) => {
          if (res.redirect_url) {
            window.location.href = res.redirect_url;
          }
        },
        error: () => {
          this.errorMessage =
            "Unable to start the TikTok connection.";
        },
      });
  }

  async disconnect(): Promise<void> {
    if (!this.selectedSiteId || !this.connectionStatus?.connected) {
      this.errorMessage = "No connected TikTok account found.";
      return;
    }

    // Check for other connections first
    this.loadingConnect = true;
    const connectionInfo = await this.checkOtherConnections();
    this.loadingConnect = false;

    // Show appropriate confirmation dialog
    const confirmed = await this.showDisconnectConfirmation(connectionInfo.is_local_only);
    if (!confirmed) {
      return;
    }

    this.loadingConnect = true;
    this.errorMessage = "";

    this.coreService.postData('tiktok/disconnect', {
      platform_id: this.connectionStatus!.platform_id
    })
      .pipe(
        finalize(() => {
          this.loadingConnect = false;
        })
      )
      .subscribe({
        next: (res) => {
          if (res.success) {
            // Update component state
            this.connectionStatus = {
              site_id: this.selectedSiteId!,
              platform_id: this.connectionStatus!.platform_id,  // Keep the same platform ID after disconnection
              connected: false,
              social_media_id: null,
              connected_at: null,
              last_validated_at: null
            };
            this.connected = false;
            
            // Show appropriate success message based on disconnection type
            const message = res.revoked 
              ? 'TikTok account disconnected successfully' 
              : 'Site disconnected from TikTok (other sites remain connected)';
            this.coreService.showSnackbar(message);
            
            // Notify parent
            this.platformDisconnected.emit();
          } else {
            this.errorMessage = res.message || "Unable to disconnect TikTok account.";
          }
        },
        error: (err) => {
          this.errorMessage = err.error?.message || "Unable to disconnect TikTok account.";
        },
      });
  }

  private async checkOtherConnections(): Promise<{ other_connections: number, is_local_only: boolean }> {
    if (!this.connectionStatus?.platform_id) {
      return { other_connections: 0, is_local_only: false };
    }

    return new Promise((resolve) => {
      this.coreService.postData('tiktok/check-other-connections', {
        platform_id: this.connectionStatus!.platform_id
      }).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: () => {
          // Default to full disconnection if check fails
          resolve({ other_connections: 0, is_local_only: false });
        }
      });
    });
  }

  private loadSites(): void {
    const companyId = Number(this.companyID);

    if (!companyId) {
      this.sites = [];
      this.selectedSiteId = null;
      return;
    }

    this.coreService.getSites(companyId)
      .subscribe({
        next: (response: {
          siteDomains?: Array<{
            ID: number;
            Domain: string | null;
            isApproved: string;
          }>;
        }) => {
          this.sites = (response.siteDomains || [])
            .filter(
              (site) =>
                site.isApproved === "Approved" &&
                site.Domain !== "default"
            )
            .map((site) => ({
              id: Number(site.ID),
              domain:
                site.Domain || `Site #${site.ID}`,
            }));

          // Process pending preselections if they exist
          if (this.pendingPreselectedSiteId) {
            this.handlePreselectedSite(this.pendingPreselectedSiteId);
            this.pendingPreselectedSiteId = null;
          } else if (this.pendingPreselectedPlatformId) {
            this.handlePreselectedPlatform(this.pendingPreselectedPlatformId);
            this.pendingPreselectedPlatformId = null;
          } else if (
            !this.selectedSiteId &&
            this.sites.length === 1
          ) {
            this.selectedSiteId = this.sites[0].id;
            this.loadConnectionStatus(
              this.selectedSiteId
            );
          }
        },
        error: () => {
          this.sites = [];
          this.errorMessage =
            "Unable to load the available Publio Sites.";
        },
      });
  }

  private loadConnectionStatus(
    siteId: number
  ): void {
    this.loadingStatus = true;
    this.connectionStatus = null;

    this.coreService.getData(
      `/tiktok/platforms`,
      {
        company_id: Number(this.companyID)
      }
    )
      .pipe(
        finalize(() => {
          this.loadingStatus = false;
        })
      )
      .subscribe({
        next: (platforms: any[]) => {
          // Find platform for the selected site
          const platform = platforms.find(p => p.ms_contentsite_id === siteId);
          
          if (platform) {
            this.connectionStatus = {
              site_id: siteId,
              platform_id: platform.id,  // Store the actual platform ID for disconnection
              connected: true,
              social_media_id: platform.social_media_id,
              connected_at: null,
              last_validated_at: null
            };
            this.connected = true;
          } else {
            this.connectionStatus = {
              site_id: siteId,
              platform_id: 0,  // No platform ID when not connected
              connected: false,
              social_media_id: null,
              connected_at: null,
              last_validated_at: null
            };
            this.connected = false;
          }
        },
        error: () => {
          this.connectionStatus = null;
          this.errorMessage =
            "Unable to load the TikTok connection status.";
        },
      });
  }



  private handlePreselectedSite(siteId: string): void {
    // Check if this site is in our sites list
    const siteExists = this.sites.some(site => site.id === Number(siteId));
    
    if (siteExists) {
      // Set the selected site ID
      this.selectedSiteId = Number(siteId);
      // Load connection status for this site
      this.loadConnectionStatus(Number(siteId));
    } else {
      console.warn('Preselected site not found in available sites list');
    }
  }

  private handlePreselectedPlatform(platformId: string): void {
    // Get the platform details to find the associated site
    this.coreService.getData(`/manageAccount/platform/edit/${platformId}`, {
      companyID: Number(this.companyID)
    }).subscribe({
      next: (platform: any) => {
        if (platform.ms_contentsite_id) {
          // Check if this site is in our sites list
          const siteExists = this.sites.some(site => site.id === platform.ms_contentsite_id);
          
          if (siteExists) {
            // Set the selected site ID
            this.selectedSiteId = platform.ms_contentsite_id;
            // Load connection status for this site
            this.loadConnectionStatus(platform.ms_contentsite_id);
          } else {
            console.warn('Preselected site not found in available sites list');
          }
        }
      },
      error: (error) => {
        console.warn('Could not load platform details for preselection', error);
      }
    });
  }
}